
import { UserRole, RequestStatus, SubscriptionTier, MeetingPlatform, SchedulingStatus } from '../types.ts';

const STORAGE_KEYS = {
  USERS: 'cc_users',
  STUDENT_PROFILES: 'cc_student_profiles',
  PROF_PROFILES: 'cc_prof_profiles',
  REQUESTS: 'cc_requests',
  CURRENT_USER: 'cc_current_user',
  JOBS: 'cc_jobs',
  EVENTS: 'cc_events',
  SETTINGS: 'cc_platform_settings'
};

let _IN_MEMORY_DB: Record<string, any> = {};

const getFromStorage = (key: string, defaultValue: any) => {
  return _IN_MEMORY_DB[key] || defaultValue;
};

const setToStorage = (key: string, data: any) => {
  _IN_MEMORY_DB[key] = data;
};

export const dbService = {
  seedInitialData: () => {
    _IN_MEMORY_DB = {};

    const mockUsers = [
      { id: 'student_alex', email: 'alex.doe@stanford.edu', fullName: 'Alex Doe', role: UserRole.STUDENT, tier: SubscriptionTier.ADVANCED, createdAt: Date.now() },
      { id: 'student_sarah', email: 'sarah.c@uwaterloo.ca', fullName: 'Sarah Chen', role: UserRole.STUDENT, tier: SubscriptionTier.STANDARD, createdAt: Date.now() },
      // New Campus Peers for Demo (UofT)
      { id: 'student_david', email: 'david.k@utoronto.ca', fullName: 'David Kim', role: UserRole.STUDENT, tier: SubscriptionTier.STANDARD, createdAt: Date.now() },
      { id: 'student_emily', email: 'emily.w@utoronto.ca', fullName: 'Emily Wong', role: UserRole.STUDENT, tier: SubscriptionTier.BASIC, createdAt: Date.now() },
      { id: 'student_michael', email: 'm.chen@utoronto.ca', fullName: 'Michael Chen', role: UserRole.STUDENT, tier: SubscriptionTier.ADVANCED, createdAt: Date.now() },
      { id: 'student_jessica', email: 'j.park@utoronto.ca', fullName: 'Jessica Park', role: UserRole.STUDENT, tier: SubscriptionTier.BASIC, createdAt: Date.now() },
      // Professionals
      { id: 'pro_liam', email: 'liam.trades@hft.com', fullName: 'Liam Vance', role: UserRole.PROFESSIONAL, tier: SubscriptionTier.ADVANCED, createdAt: Date.now() },
      { id: 'pro_sophia', email: 'sophia.q@quant.ai', fullName: 'Sophia Zhang', role: UserRole.PROFESSIONAL, tier: SubscriptionTier.STANDARD, createdAt: Date.now() },
      { id: 'pro_jordan', email: 'jordan.lee@demo.com', fullName: 'Jordan Lee', role: UserRole.PROFESSIONAL, tier: SubscriptionTier.STANDARD, createdAt: Date.now() },
    ];
    setToStorage(STORAGE_KEYS.USERS, mockUsers);

    setToStorage(STORAGE_KEYS.STUDENT_PROFILES, [
      {
        userId: 'student_alex',
        fullName: 'Alex Doe',
        school: 'University of Toronto',
        campus: 'St. George',
        degree: 'Undergraduate',
        major: 'Software Engineering',
        yearOfStudy: '3rd Year',
        gpa: '3.9',
        interests: ['Low Latency Systems', 'C++', 'Distributed State Machines'],
        mentorshipTopics: ['C++ Optimization', 'Career in HFT', 'Kernel Bypass'],
        hobbies: ['Competitive Chess', 'Rock Climbing'],
        goals: 'Breaking into high-frequency trading (HFT) as a systems architect.',
        city: 'Toronto',
        networkingCity: 'Chicago',
        aspiringRole: 'HFT Systems Architect',
        resumeUrl: 'https://example.com/alex-doe-resume.pdf',
        timezone: 'EST',
        bio: 'I am a 3rd-year Software Engineering student at UofT focused on high-performance computing. I am currently building a latch-free order book in C++.',
        workHistory: [
          { role: 'Software Engineer Intern', company: 'Amazon', location: 'Vancouver, BC', duration: '4 Months' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'student_david',
        fullName: 'David Kim',
        school: 'University of Toronto',
        campus: 'St. George',
        degree: 'Undergraduate',
        major: 'Computer Science',
        yearOfStudy: '4th Year',
        gpa: '3.95',
        interests: ['Machine Learning', 'NLP', 'System Design'],
        mentorshipTopics: ['AI Research Paths', 'FAANG Interview Prep'],
        hobbies: ['Hackathons', 'Gaming'],
        goals: 'Joining a Big Tech AI division.',
        city: 'Toronto',
        networkingCity: 'San Francisco',
        aspiringRole: 'AI Research Scientist',
        timezone: 'EST',
        bio: 'Incoming SWE at Google. I have done 3 internships and love mentoring younger students on cracking the coding interview.',
        workHistory: [
          { role: 'SWE Intern', company: 'Google', location: 'Waterloo, ON', duration: '4 Months' },
          { role: 'Research Assistant', company: 'Vector Institute', location: 'Toronto, ON', duration: '8 Months' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'student_emily',
        fullName: 'Emily Wong',
        school: 'University of Toronto',
        campus: 'Scarborough',
        degree: 'Undergraduate',
        major: 'Finance',
        yearOfStudy: '4th Year',
        gpa: '3.8',
        interests: ['Capital Markets', 'Private Equity', 'ESG Investing'],
        mentorshipTopics: ['Investment Banking Recruiting', 'Women in Finance'],
        hobbies: ['Tennis', 'Debate Club'],
        goals: 'Securing a full-time Analyst role in Investment Banking.',
        city: 'Toronto',
        networkingCity: 'New York',
        aspiringRole: 'Investment Banking Analyst',
        timezone: 'EST',
        bio: 'Rotman Commerce student specializing in Finance and Economics. I\'ve navigated the recruit cycle twice and can help with behavioral prep.',
        workHistory: [
          { role: 'IB Summer Analyst', company: 'RBC Capital Markets', location: 'Toronto, ON', duration: '4 Months' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'student_michael',
        fullName: 'Michael Chen',
        school: 'University of Toronto',
        campus: 'St. George',
        degree: 'Graduate (Master\'s)',
        major: 'Data Science',
        yearOfStudy: 'Graduate (Master\'s)',
        gpa: '4.0',
        interests: ['Big Data', 'Healthcare Tech', 'Python'],
        mentorshipTopics: ['Data Science Portfolios', 'HealthTech Trends'],
        hobbies: ['Photography', 'Hiking'],
        goals: 'Lead Data Scientist in HealthTech.',
        city: 'Toronto',
        networkingCity: 'Boston',
        aspiringRole: 'Data Scientist',
        timezone: 'EST',
        bio: 'Master\'s student with a background in Statistics. Passionate about applying AI to personalized medicine.',
        workHistory: [
           { role: 'Data Scientist Co-op', company: 'UHN', location: 'Toronto, ON', duration: '8 Months' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'student_jessica',
        fullName: 'Jessica Park',
        school: 'University of Toronto',
        campus: 'Mississauga',
        degree: 'Undergraduate',
        major: 'Biology',
        yearOfStudy: '2nd Year',
        gpa: '3.6',
        interests: ['Genetics', 'Research'],
        mentorshipTopics: ['Med School Applications', 'Lab Experience'],
        hobbies: ['Painting', 'Violin'],
        goals: 'Medical School.',
        city: 'Mississauga',
        networkingCity: 'Toronto',
        aspiringRole: 'Medical Researcher',
        timezone: 'EST',
        bio: 'Aspiring doctor. Currently looking for research opportunities in wet labs.',
        workHistory: [], 
        updatedAt: Date.now()
      },
      {
        userId: 'student_sarah',
        fullName: 'Sarah Chen',
        school: 'University of Waterloo',
        degree: 'Undergraduate',
        major: 'Economics',
        yearOfStudy: '4th Year',
        gpa: '3.7',
        interests: ['M&A', 'Asset Management'],
        mentorshipTopics: ['Breaking into Capital Markets', 'Networking Tips'],
        hobbies: ['Marathon Running', 'Cello'],
        goals: 'Securing a full-time role in Capital Markets at a bulge bracket bank.',
        city: 'Waterloo',
        networkingCity: 'London',
        aspiringRole: 'Equity Research Associate',
        resumeUrl: 'https://cdn.filestackcontent.com/preview-resume-template', // ADDED MOCK RESUME
        timezone: 'EST',
        bio: 'Final year Economics student at Waterloo. I have completed two co-ops in corporate finance and am now looking to transition into front-office capital markets roles.',
        workHistory: [
          { role: 'Finance Intern', company: 'Sun Life', location: 'Toronto, ON', duration: '4 Months' }
        ],
        updatedAt: Date.now()
      }
    ]);

    setToStorage(STORAGE_KEYS.PROF_PROFILES, [
      {
        userId: 'pro_liam',
        fullName: 'Liam Vance',
        company: 'Citadel Securities',
        title: 'Lead HFT Architect',
        industry: 'Finance',
        yearsExperience: 12,
        topics: ['C++ Optimization', 'Kernel Tuning', 'HFT Architecture'],
        hobbies: ['Track Racing', 'Coffee Roasting'],
        city: 'Chicago',
        bio: 'Over a decade of experience building execution engines. I specialize in Linux kernel bypass and FPGA acceleration.',
        maxMeetingsPerWeek: 3,
        meetingsThisWeek: 1,
        points: 890,
        isVerified: true,
        gradUniversity: 'University of Toronto',
        gradUniversityYear: '2012',
        highSchool: 'Upper Canada College',
        highSchoolYear: '2008',
        preferredPlatform: MeetingPlatform.ZOOM,
        targetStudentGPA: '3.8+',
        targetStudentIndustry: 'Software Engineering',
        targetStudentDegree: 'any',
        workHistory: [
          { role: 'Lead Architect', company: 'Citadel Securities', location: 'Chicago, IL', duration: '4 Years' },
          { role: 'Senior Systems Engineer', company: 'Optiver', location: 'Amsterdam, NL', duration: '5 Years' },
          { role: 'Software Engineer', company: 'IBM', location: 'Toronto, ON', duration: '3 Years' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'pro_sophia',
        fullName: 'Sophia Zhang',
        company: 'Quant AI',
        title: 'Research Lead',
        industry: 'Tech',
        yearsExperience: 6,
        topics: ['ML Pipelines', 'AI Research', 'PhD Applications'],
        hobbies: ['Scuba Diving', 'Oil Painting'],
        city: 'Toronto',
        bio: 'Bridging the gap between academic AI research and production-grade financial models.',
        maxMeetingsPerWeek: 5,
        meetingsThisWeek: 1,
        points: 120,
        isVerified: true,
        gradUniversity: 'Stanford University',
        gradUniversityYear: '2018',
        highSchool: 'Bayview Secondary School',
        highSchoolYear: '2014',
        preferredPlatform: MeetingPlatform.TEAMS,
        targetStudentGPA: 'any',
        targetStudentIndustry: 'any',
        targetStudentDegree: 'Graduate (Master\'s)',
        workHistory: [
          { role: 'Research Lead', company: 'Quant AI', location: 'Toronto, ON', duration: '2 Years' },
          { role: 'Senior ML Scientist', company: 'Vector Institute', location: 'Toronto, ON', duration: '3 Years' },
          { role: 'Graduate Researcher', company: 'Stanford AI Lab', location: 'Stanford, CA', duration: '1 Year' }
        ],
        updatedAt: Date.now()
      },
      {
        userId: 'pro_jordan',
        fullName: 'Jordan Lee',
        company: 'Goldman Sachs',
        title: 'VP, Capital Markets',
        industry: 'Finance',
        yearsExperience: 8,
        topics: ['M&A Deal Flow', 'Equity Financing', 'Networking Strategy'],
        hobbies: ['Jazz Piano', 'Sailing'],
        city: 'New York',
        bio: 'Specializing in TMT capital raises. I transitioned from a boutique firm in Toronto to NYC.',
        maxMeetingsPerWeek: 4,
        meetingsThisWeek: 0,
        points: 450,
        isVerified: true,
        gradUniversity: 'University of Waterloo',
        gradUniversityYear: '2016',
        highSchool: 'Waterloo Collegiate Institute',
        highSchoolYear: '2012',
        preferredPlatform: MeetingPlatform.GOOGLE,
        targetStudentGPA: 'any',
        targetStudentIndustry: 'any',
        targetStudentDegree: 'any',
        workHistory: [
          { role: 'VP, Capital Markets', company: 'Goldman Sachs', location: 'New York, NY', duration: '3 Years' },
          { role: 'Associate', company: 'J.P. Morgan', location: 'London, UK', duration: '3 Years' },
          { role: 'Analyst', company: 'RBC Capital Markets', location: 'Toronto, ON', duration: '2 Years' }
        ],
        updatedAt: Date.now()
      }
    ]);

    const mockRequests = [
      {
        id: 'req_alex_liam',
        studentId: 'student_alex',
        professionalId: 'pro_liam',
        status: RequestStatus.ACCEPTED,
        synergyScore: 98,
        matchRationale: 'Synergy Apex: Alex is building an HFT order book; Liam is a Lead HFT Architect.',
        synergyThesis: 'Alex meets your strict 3.8+ GPA filter (3.9) and industry target (Software Engineering). His focus on C++ order books directly synchronizes with your Citadel lead role.',
        studentNote: 'Working on a low-latency state machine in C++. Would love to discuss kernel-bypass networking for order matching.',
        schedulingStatus: SchedulingStatus.CONFIRMED,
        scheduledTime: Date.now() + 86400000 * 2,
        meetingPlatform: MeetingPlatform.ZOOM,
        meetingLink: 'https://coffeeconnex.ai/meeting/hft-apex-room',
        messages: [
          { id: 'm1', senderId: 'student_alex', text: "Hi Liam! Thanks for connecting. I've been reading your paper on latch-free queues. Thrilled to chat.", timestamp: Date.now() - 3600000 * 2 },
          { id: 'm2', senderId: 'pro_liam', text: "Hey Alex. Impressive profile. Let's dig into memory barriers on our call.", timestamp: Date.now() - 3600000 }
        ],
        createdAt: Date.now() - 86400000,
        updatedAt: Date.now()
      },
      {
        id: 'req_sarah_jordan',
        studentId: 'student_sarah',
        professionalId: 'pro_jordan',
        status: RequestStatus.PENDING,
        synergyScore: 92,
        matchRationale: 'Alumni Synergy: Both Sarah and Jordan are University of Waterloo graduates.',
        synergyThesis: 'Sarah aligns with your open mentorship protocol (Any/Any filters). As a Waterloo alumna, she shares your institutional lineage, easing the relational bridge for capital markets recruiting.',
        studentNote: 'Hi Jordan! I am a 4th year Econ student at Waterloo. I saw your transition to Goldman NYC and would love to ask about the preparation required for bulge bracket recruiting from a Canadian university.',
        schedulingStatus: SchedulingStatus.NOT_STARTED,
        messages: [],
        createdAt: Date.now() - 3600000 * 4,
        updatedAt: Date.now()
      }
    ];
    setToStorage(STORAGE_KEYS.REQUESTS, mockRequests);
  },

  getUsers: () => getFromStorage(STORAGE_KEYS.USERS, []),
  saveUser: async (user: any) => {
    const users = dbService.getUsers();
    const index = users.findIndex(u => u.id === user.id);
    if (index >= 0) users[index] = { ...users[index], ...user };
    else users.push({ ...user, tier: user.tier || SubscriptionTier.BASIC });
    setToStorage(STORAGE_KEYS.USERS, users);
  },
  deleteUser: (userId: string) => {
    const users = dbService.getUsers().filter(u => u.id !== userId);
    setToStorage(STORAGE_KEYS.USERS, users);
  },
  findUserByEmail: (email: string) => dbService.getUsers().find(u => u.email === email),
  getCurrentUser: () => getFromStorage(STORAGE_KEYS.CURRENT_USER, null),
  setCurrentUser: (user: any) => setToStorage(STORAGE_KEYS.CURRENT_USER, user),
  getStudentProfile: (userId: string) => getFromStorage(STORAGE_KEYS.STUDENT_PROFILES, []).find(p => p.userId === userId),
  getAllStudentProfiles: () => getFromStorage(STORAGE_KEYS.STUDENT_PROFILES, []),
  saveStudentProfile: async (profile: any) => {
    const profiles = getFromStorage(STORAGE_KEYS.STUDENT_PROFILES, []);
    const index = profiles.findIndex(p => p.userId === profile.userId);
    if (index >= 0) profiles[index] = profile;
    else profiles.push(profile);
    setToStorage(STORAGE_KEYS.STUDENT_PROFILES, profiles);
  },
  getProfessionalProfile: (userId: string) => getFromStorage(STORAGE_KEYS.PROF_PROFILES, []).find(p => p.userId === userId),
  getAllProfessionalProfiles: () => getFromStorage(STORAGE_KEYS.PROF_PROFILES, []),
  saveProfessionalProfile: async (profile: any) => {
    const profiles = getFromStorage(STORAGE_KEYS.PROF_PROFILES, []);
    const index = profiles.findIndex(p => p.userId === profile.userId);
    if (index >= 0) profiles[index] = profile;
    else profiles.push(profile);
    setToStorage(STORAGE_KEYS.PROF_PROFILES, profiles);
  },
  getRequests: () => getFromStorage(STORAGE_KEYS.REQUESTS, []),
  getRequestsForUser: (userId: string, role: string) => {
    const all = dbService.getRequests();
    return role === UserRole.STUDENT ? all.filter(r => r.studentId === userId) : all.filter(r => r.professionalId === userId);
  },
  saveRequest: async (request: any) => {
    const requests = dbService.getRequests();
    const index = requests.findIndex(r => r.id === request.id);
    if (index >= 0) requests[index] = request;
    else { if (!request.messages) request.messages = []; requests.push(request); }
    setToStorage(STORAGE_KEYS.REQUESTS, requests);
  },
  addMessageToRequest: (requestId: string, message: { senderId: string, text: string }) => {
    const requests = dbService.getRequests();
    const index = requests.findIndex(r => r.id === requestId);
    if (index >= 0) {
      if (!requests[index].messages) requests[index].messages = [];
      const newMsg = { id: Math.random().toString(36).substr(2, 9), ...message, timestamp: Date.now() };
      requests[index].messages.push(newMsg);
      requests[index].updatedAt = Date.now();
      setToStorage(STORAGE_KEYS.REQUESTS, requests);
      return requests[index];
    }
    return null;
  },
  markAsRead: (requestId: string, userId: string, role: string) => {
    const requests = dbService.getRequests();
    const index = requests.findIndex(r => r.id === requestId);
    if (index >= 0) {
      if (role === UserRole.STUDENT) requests[index].studentLastRead = Date.now();
      else requests[index].proLastRead = Date.now();
      setToStorage(STORAGE_KEYS.REQUESTS, requests);
    }
  },
  hasUnreadMessages: (userId: string, role: string) => {
    const requests = dbService.getRequestsForUser(userId, role);
    return requests.some(r => {
      if (!r.messages || r.messages.length === 0) return false;
      const lastMsgTime = r.messages[r.messages.length - 1].timestamp;
      const lastRead = role === UserRole.STUDENT ? (r.studentLastRead || 0) : (r.proLastRead || 0);
      return lastMsgTime > lastRead && r.messages[r.messages.length - 1].senderId !== userId;
    });
  },
  resetWeeklyMeetings: (userId: string) => {
    const profiles = getFromStorage(STORAGE_KEYS.PROF_PROFILES, []);
    const index = profiles.findIndex(p => p.userId === userId);
    if (index >= 0) {
      profiles[index] = { ...profiles[index], meetingsThisWeek: 0 };
      setToStorage(STORAGE_KEYS.PROF_PROFILES, profiles);
    }
  },
  getJobs: () => getFromStorage(STORAGE_KEYS.JOBS, []),
  saveJobs: (jobs: any) => setToStorage(STORAGE_KEYS.JOBS, jobs),
  getEvents: () => getFromStorage(STORAGE_KEYS.EVENTS, []),
  saveEvents: (events: any) => setToStorage(STORAGE_KEYS.EVENTS, events),
  getSettings: () => getFromStorage(STORAGE_KEYS.SETTINGS, { logoUrl: '' }),
  saveSettings: (settings: any) => setToStorage(STORAGE_KEYS.SETTINGS, settings)
};
